import { SystemEntity } from "@erp/shared-types";
import type { SocketType } from "../../types";
import { generateSocketsFromSchema } from "../socket-type-inference";
import { uniqueClassificationSlugIds } from "../classificationSlug";
import {
  CLASSIFY_EMAIL_ENUM_CATEGORIES_CONFIG_KEY,
  CLASSIFY_EMAIL_RESERVED_SOCKET_IDS,
  classifyEmailReservedOutputSchema,
} from "../../steps/communications/classify-email.step";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
  supportsSocketIdV2,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { configEntryList } from "./socketDefinition";

const CLASSIFY_EMAIL_STEP_TYPE = "classify_email";

const ARRAY_OF_CLASSIFICATION_ITEMS: SocketType = "array<json>" as SocketType;

export interface ClassifyEmailCategoryEntry {
  id: string;
  name: string;
}

export interface ClassifyEmailResultItem {
  categoryId: string;
  categoryName: string;
  confidence: number;
  source: string;
  document: unknown;
  documentName: string | null;
  reasoning: string;
}

/**
 * Snapshot CoreEnum options into step config so the sync socket generator
 * can emit per-category sockets without a DB lookup. Runtime classification
 * still loads the live enum; this list only drives canvas sockets.
 */
export function snapshotEnumCategories(
  options: Array<{ id?: unknown; name?: unknown }> | undefined,
): ClassifyEmailCategoryEntry[] {
  if (!options) return [];
  const entries: ClassifyEmailCategoryEntry[] = [];
  for (const option of options) {
    if (typeof option.id !== "string" || option.id.length === 0) continue;
    entries.push({
      id: option.id,
      name:
        typeof option.name === "string" && option.name.length > 0
          ? option.name
          : option.id,
    });
  }
  return entries;
}

export function enumCategoriesEqual(
  value: unknown,
  expected: ClassifyEmailCategoryEntry[],
): boolean {
  if (!Array.isArray(value) || value.length !== expected.length) return false;
  return value.every((item, index) => {
    const other = expected[index];
    if (other === undefined) return false;
    if (typeof item !== "object" || item === null) return false;
    const record = item as Record<string, unknown>;
    return record.id === other.id && record.name === other.name;
  });
}

function reservedClassifyEmailSockets(): DynamicSocketDefinition[] {
  return generateSocketsFromSchema(
    classifyEmailReservedOutputSchema,
    "output",
  ).map((socket) => ({
    id: socket.id,
    name: socket.name,
    type: socket.type,
    required: socket.required,
    description: socket.description,
  }));
}

function addCategoryEntry(
  entries: ClassifyEmailCategoryEntry[],
  seen: Set<string>,
  id: string,
  name: string,
): void {
  if (id.length === 0 || seen.has(id)) return;
  seen.add(id);
  entries.push({ id, name });
}

function collectEnumSnapshotEntries(
  config: Record<string, unknown>,
  entries: ClassifyEmailCategoryEntry[],
  seen: Set<string>,
): void {
  for (const category of configEntryList<{ id?: unknown; name?: unknown }>(
    config[CLASSIFY_EMAIL_ENUM_CATEGORIES_CONFIG_KEY],
  )) {
    if (typeof category.id !== "string") continue;
    const name =
      typeof category.name === "string" && category.name.length > 0
        ? category.name
        : category.id;
    addCategoryEntry(entries, seen, category.id, name);
  }
}

function collectInlineCategoryEntries(
  config: Record<string, unknown>,
  entries: ClassifyEmailCategoryEntry[],
  seen: Set<string>,
): void {
  const names: string[] = [];
  for (const category of configEntryList<{ name?: unknown }>(
    config.categories,
  )) {
    if (typeof category.name === "string") {
      names.push(category.name);
    }
  }
  const ids = uniqueClassificationSlugIds(names);
  names.forEach((name, index) => {
    const id = ids[index];
    if (id === undefined) return;
    addCategoryEntry(entries, seen, id, name);
  });
}

function collectCategoryEntries(
  config: Record<string, unknown>,
): ClassifyEmailCategoryEntry[] {
  const entries: ClassifyEmailCategoryEntry[] = [];
  const seen = new Set<string>();

  if (config.classificationSource === "enum") {
    collectEnumSnapshotEntries(config, entries, seen);
  } else {
    collectInlineCategoryEntries(config, entries, seen);
  }

  if (config.includeOther !== false) {
    addCategoryEntry(entries, seen, "other", "Other");
  }

  return entries;
}

export interface ResolvedClassifyEmailOutputSockets {
  sockets: DynamicSocketDefinition[];
  /** Output socket id for each configured category id. */
  socketIdByCategoryId: Map<string, string>;
}

/**
 * Generate output sockets for `classify_email`.
 *
 * Always emits the original parallel-array sockets (categoryId, categoryName,
 * …) so existing graphs keep working. Adds one `array<json>` socket per
 * configured category (inline names, or a snapshot of CoreEnum options) plus
 * `other` when that fallback is enabled.
 */
export function resolveClassifyEmailOutputSockets(
  config: Record<string, unknown>,
): ResolvedClassifyEmailOutputSockets {
  const categories = collectCategoryEntries(config);

  const { sockets: categorySockets, assignedIds } = buildDynamicSockets({
    reserved: CLASSIFY_EMAIL_RESERVED_SOCKET_IDS,
    version: effectiveSocketIdVersion(CLASSIFY_EMAIL_STEP_TYPE, config),
    markedFallback: supportsSocketIdV2(CLASSIFY_EMAIL_STEP_TYPE),
    entries: categories.map((category) => ({
      name: category.id,
      socket: {
        name: category.name,
        type: ARRAY_OF_CLASSIFICATION_ITEMS,
        required: false,
        description: `Items classified as "${category.name}"`,
      },
    })),
  });

  const socketIdByCategoryId = new Map<string, string>();
  categories.forEach((category, index) => {
    const assigned = assignedIds[index];
    if (assigned !== null && assigned !== undefined) {
      socketIdByCategoryId.set(category.id, assigned);
    }
  });

  return {
    sockets: [...reservedClassifyEmailSockets(), ...categorySockets],
    socketIdByCategoryId,
  };
}

export function generateClassifyEmailOutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  return resolveClassifyEmailOutputSockets(config).sockets;
}

export function buildClassifyEmailParallelArrays(
  items: ClassifyEmailResultItem[],
): Record<string, unknown> {
  return {
    categoryId: items.map((item) => item.categoryId),
    categoryName: items.map((item) => item.categoryName),
    confidence: items.map((item) => item.confidence),
    source: items.map((item) => item.source),
    document: items.map((item) => item.document ?? null),
    documentName: items.map((item) => item.documentName ?? null),
    reasoning: items.map((item) => item.reasoning ?? ""),
  };
}

/**
 * Handler payload: the reserved parallel arrays plus one array per
 * category socket. Untaken categories get `[]` so fanout-zero / empty
 * propagation still works without renaming the existing sockets.
 */
export function buildClassifyEmailOutputPayload(
  config: Record<string, unknown>,
  items: ClassifyEmailResultItem[],
): Record<string, unknown> {
  const { socketIdByCategoryId } = resolveClassifyEmailOutputSockets(config);
  const payload: Record<string, unknown> = {
    ...buildClassifyEmailParallelArrays(items),
  };

  for (const socketId of socketIdByCategoryId.values()) {
    payload[socketId] = [];
  }

  for (const item of items) {
    const socketId = socketIdByCategoryId.get(item.categoryId);
    if (socketId === undefined) continue;
    const bucket = payload[socketId];
    if (!Array.isArray(bucket)) continue;
    bucket.push(item);
  }

  return payload;
}

/** Exported for tests that assert the document array socket type. */
export const CLASSIFY_EMAIL_DOCUMENT_SOCKET_TYPE: SocketType =
  `array<entity_reference:${SystemEntity.CORE_DOCUMENT}>` as SocketType;
